import React, { useState, useEffect } from "react";
import { useBusinessPlan } from "../App";
import { useCurrency } from "../contexts/CurrencyContext";
import axios from "axios";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./ui/card";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import ExportButton from "./ui/ExportButton";
import ChartWithExport from "./ui/ChartWithExport";
import TooltipLabel from "./ui/TooltipLabel";
import { 
  BarChart3, 
  TrendingUp, 
  TrendingDown,
  DollarSign, 
  Target, 
  Activity, 
  ArrowUpRight, 
  ArrowDownRight,
  RefreshCw,
  Eye,
  Calendar,
  PieChart,
  Zap,
  Users,
  Building2,
  AlertTriangle,
  CheckCircle,
  Clock,
  Shield,
  Percent,
  Wallet,
  TrendingDown as BurnIcon,
  MinusCircle,
  CheckCircle2,
  Filter
} from "lucide-react";
import { 
  LineChart, 
  Line, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  Legend,
  ResponsiveContainer, 
  BarChart, 
  Bar, 
  PieChart as RechartsPieChart, 
  Pie, 
  Cell,
  AreaChart,
  Area,
  ComposedChart,
  Radar,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis
} from "recharts";
import { CircularProgressbar, buildStyles } from 'react-circular-progressbar';
import GaugeChart from 'react-gauge-chart';
import 'react-circular-progressbar/dist/styles.css';
import { usePlanningYears } from '../hooks/usePlanningYears';
import { outputTooltips } from '../utils/outputSectionsTooltips';
import { formatCurrency as formatCurrencyUtil, formatPercentage as formatPercentageUtil } from '../utils/sharedCalculations';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const EnhancedKPIDashboard = () => {
  const { currentPlan } = useBusinessPlan();
  const { formatCurrency: formatCurrencyContext } = useCurrency();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [selectedTimeframe, setSelectedTimeframe] = useState('5y');
  const [selectedMetric, setSelectedMetric] = useState('all');
  
  // Financial Management Integration State
  const [financialKPIs, setFinancialKPIs] = useState(null);
  const [integrationData, setIntegrationData] = useState(null);
  
  // Data state
  const [dashboardData, setDashboardData] = useState({});
  const [financialData, setFinancialData] = useState(null);
  const [advancedCalculations, setAdvancedCalculations] = useState(null);
  const [costData, setCostData] = useState([]);
  const [strategicData, setStrategicData] = useState({});
  
  // PHASE 1: New KPIs and Filters state
  const [financialHealthKPIs, setFinancialHealthKPIs] = useState(null);
  const [selectedYear, setSelectedYear] = useState('all');
  const [selectedQuarter, setSelectedQuarter] = useState('all');
  const [selectedMonth, setSelectedMonth] = useState('all');
  
  // PHASE 2: Executive Summary Enhancements state
  const [planningHealthScore, setPlanningHealthScore] = useState(null);
  const [milestones, setMilestones] = useState(null);
  const [scenarioComparison, setScenarioComparison] = useState(null);
  const [resourceAllocation, setResourceAllocation] = useState(null);
  const [planningCoverage, setPlanningCoverage] = useState(null);
  const [keyAssumptions, setKeyAssumptions] = useState(null);

  // Use dynamic planning years
  const planningData = usePlanningYears(currentPlan?.id);
  const { years, loading: yearsLoading } = planningData;

  const COLORS = ['#8884d8', '#82ca9d', '#ffc658', '#ff7c7c', '#8dd1e1', '#d084d0', '#ffb347', '#87ceeb'];

  // Format currency
  const formatCurrency = (amount, compact = false) => {
    return formatCurrencyContext(amount, currentPlan?.currency || 'USD', null, compact);
  };

  // Format percentage
  const formatPercentage = (value) => {
    return formatPercentageUtil(value, 1);
  };

  // Load comprehensive dashboard data
  const loadDashboardData = async () => {
    if (!currentPlan?.id) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      console.log('Loading enhanced KPI dashboard data...');

      const promises = [];
      
      // Financial data for all years
      years.forEach(year => {
        promises.push(
          axios.get(`${BACKEND_URL}/api/v3/calculations/financial-statements/${currentPlan.id}?year=${year}`)
            .then(res => ({ [`financial_${year}`]: res.data }))
            .catch(err => ({ [`financial_${year}`]: null }))
        );
      });

      // Advanced calculations
      promises.push(
        Promise.all([
          axios.get(`${BACKEND_URL}/api/v3/calculations/break-even/${currentPlan.id}`),
          axios.get(`${BACKEND_URL}/api/v3/calculations/roi-analysis/${currentPlan.id}`),
          axios.get(`${BACKEND_URL}/api/v3/calculations/cash-flow/${currentPlan.id}`)
        ]).then(([breakEven, roi, cashFlow]) => ({
          advancedCalculations: {
            breakEven: breakEven.data,
            roi: roi.data,  
            cashFlow: cashFlow.data
          }
        })).catch(err => ({ advancedCalculations: null }))
      );

      // Cost data
      promises.push(
        axios.get(`${BACKEND_URL}/api/v3/cost-items/${currentPlan.id}`)
          .then(res => ({ costData: res.data }))
          .catch(err => ({ costData: [] }))
      );

      const results = await Promise.all(promises);
      const combinedData = results.reduce((acc, curr) => ({ ...acc, ...curr }), {});

      // Process financial data for multi-year analysis
      const processedFinancialData = {};
      years.forEach(year => {
        if (combinedData[`financial_${year}`]) {
          processedFinancialData[year] = combinedData[`financial_${year}`];
        }
      });

      setFinancialData(processedFinancialData);
      setAdvancedCalculations(combinedData.advancedCalculations);
      setCostData(combinedData.costData || []);

      // Calculate dashboard metrics
      calculateDashboardMetrics(processedFinancialData, combinedData.advancedCalculations, combinedData.costData || []);

      console.log('✅ Enhanced KPI dashboard data loaded');
      
    } catch (err) {
      console.error('Error loading KPI dashboard data:', err);
    } finally {
      setLoading(false);
    }
  };

  // Calculate comprehensive dashboard metrics
  const calculateDashboardMetrics = (financial, advanced, costs) => {
    const metrics = {
      financial: {},
      performance: {},
      efficiency: {},
      growth: {},
      health: {}
    };

    // Multi-year financial analysis
    const multiYearData = [];
    Object.entries(financial).forEach(([year, data]) => {
      if (data?.financial_statements?.income_statement) {
        const income = data.financial_statements.income_statement;
        const keyMetrics = data.financial_statements.key_metrics || {};
        
        multiYearData.push({
          year: parseInt(year),
          revenue: income.revenue || 0,
          grossProfit: income.gross_profit || 0,
          ebitda: income.ebitda || 0,
          netIncome: income.net_income || 0,
          grossMargin: keyMetrics.gross_margin_percent || 0,
          ebitdaMargin: keyMetrics.ebitda_margin_percent || 0,
          netMargin: keyMetrics.net_margin_percent || 0,
          operatingRatio: keyMetrics.operating_ratio || 0
        });
      }
    });

    // Growth calculations
    if (multiYearData.length >= 2) {
      const latest = multiYearData[multiYearData.length - 1];
      const previous = multiYearData[multiYearData.length - 2];
      
      metrics.growth = {
        revenueGrowth: previous.revenue ? ((latest.revenue - previous.revenue) / previous.revenue) * 100 : 0,
        profitGrowth: previous.netIncome ? ((latest.netIncome - previous.netIncome) / previous.netIncome) * 100 : 0,
        ebitdaGrowth: previous.ebitda ? ((latest.ebitda - previous.ebitda) / previous.ebitda) * 100 : 0
      };
    }

    // Financial health metrics
    if (multiYearData.length > 0) {
      const current = multiYearData[multiYearData.length - 1];
      
      metrics.financial = {
        currentRevenue: current.revenue,
        currentProfit: current.netIncome,
        currentEbitda: current.ebitda,
        profitability: current.netMargin,
        efficiency: current.operatingRatio
      };

      // Health score calculation (0-100)
      let healthScore = 0;
      if (current.grossMargin > 0) healthScore += 20;
      if (current.ebitdaMargin > 0) healthScore += 20;  
      if (current.netMargin > 0) healthScore += 20;
      if (current.operatingRatio < 100) healthScore += 20;
      if (current.revenue > 0) healthScore += 20;

      metrics.health.overallScore = healthScore;
      metrics.health.profitabilityScore = Math.max(0, Math.min(100, current.netMargin * 5));
      metrics.health.efficiencyScore = Math.max(0, Math.min(100, 100 - current.operatingRatio));
    }

    // Cost analysis
    const totalMonthlyCosts = costs.reduce((sum, cost) => sum + (cost.amount || 0), 0);
    const costByCategory = {};
    costs.forEach(cost => {
      const category = cost.cost_category || 'uncategorized';
      costByCategory[category] = (costByCategory[category] || 0) + (cost.amount || 0);
    });

    metrics.performance = {
      totalMonthlyCosts: totalMonthlyCosts,
      annualCostProjection: totalMonthlyCosts * 12,
      costByCategory: costByCategory,
      costItemCount: costs.length
    };

    // Advanced calculations integration
    if (advanced?.breakEven?.break_even_analysis) {
      metrics.efficiency.breakEvenUnits = advanced.breakEven.break_even_analysis.break_even_units || 0;
      metrics.efficiency.breakEvenRevenue = advanced.breakEven.break_even_analysis.break_even_revenue || 0;
      metrics.efficiency.marginOfSafety = advanced.breakEven.break_even_analysis.margin_of_safety?.percent || 0;
    }

    if (advanced?.cashFlow?.summary) {
      metrics.financial.cashRunway = advanced.cashFlow.summary.average_cash_runway_months || 0;
      metrics.financial.operatingCashFlow = advanced.cashFlow.summary.total_operating_cash_flow || 0;
    }

    setDashboardData({ ...metrics, multiYear: multiYearData });
  };

  // Refresh data
  const handleRefresh = async () => {
    setRefreshing(true);
    await loadDashboardData();
    setRefreshing(false);
  };

  // PHASE 2: Calculate Planning Health Score
  const calculatePlanningHealthScore = () => {
    if (!financialData || !advancedCalculations || !costData) {
      return null;
    }

    try {
      // Get latest year data
      const latestYear = years && years.length > 0 ? Math.max(...years) : null;
      const latestFinancial = latestYear ? financialData[latestYear] : null;
      
      if (!latestFinancial?.financial_statements) {
        return null;
      }

      const income = latestFinancial.financial_statements.income_statement || {};
      const balance = latestFinancial.financial_statements.balance_sheet || {};
      const keyMetrics = latestFinancial.financial_statements.key_metrics || {};
      
      // Calculate component scores (0-100)
      
      // 1. Liquidity Score (25%) - Current Ratio
      let liquidityScore = 0;
      const currentRatio = balance.current_assets && balance.current_liabilities 
        ? balance.current_assets / balance.current_liabilities 
        : 0;
      if (currentRatio >= 2.0) liquidityScore = 100;
      else if (currentRatio >= 1.5) liquidityScore = 80;
      else if (currentRatio >= 1.0) liquidityScore = 60;
      else if (currentRatio >= 0.5) liquidityScore = 40;
      else liquidityScore = 20;
      
      // 2. Profitability Score (25%) - Net Margin
      let profitabilityScore = 0;
      const netMargin = keyMetrics.net_margin_percent || 0;
      if (netMargin >= 20) profitabilityScore = 100;
      else if (netMargin >= 10) profitabilityScore = 80;
      else if (netMargin >= 5) profitabilityScore = 60;
      else if (netMargin >= 0) profitabilityScore = 40;
      else profitabilityScore = 20;
      
      // 3. Leverage Score (20%) - Debt to Equity (lower is better)
      let leverageScore = 0;
      const debtToEquity = keyMetrics.debt_to_equity || 0;
      if (debtToEquity <= 0.5) leverageScore = 100;
      else if (debtToEquity <= 1.0) leverageScore = 80;
      else if (debtToEquity <= 1.5) leverageScore = 60;
      else if (debtToEquity <= 2.0) leverageScore = 40;
      else leverageScore = 20;
      
      // 4. Cash Runway Score (20%)
      let cashRunwayScore = 0;
      const cashRunway = advancedCalculations?.cashFlow?.summary?.average_cash_runway_months || 0;
      if (cashRunway >= 12) cashRunwayScore = 100;
      else if (cashRunway >= 6) cashRunwayScore = 80;
      else if (cashRunway >= 3) cashRunwayScore = 60;
      else if (cashRunway >= 1) cashRunwayScore = 40;
      else cashRunwayScore = 20;
      
      // 5. Growth Score (10%) - Revenue Growth
      let growthScore = 0;
      const revenueGrowth = dashboardData.growth?.revenueGrowth || 0;
      if (revenueGrowth >= 30) growthScore = 100;
      else if (revenueGrowth >= 20) growthScore = 80;
      else if (revenueGrowth >= 10) growthScore = 60;
      else if (revenueGrowth >= 5) growthScore = 40;
      else growthScore = 20;
      
      // Calculate weighted health score
      const healthScore = (
        liquidityScore * 0.25 +
        profitabilityScore * 0.25 +
        leverageScore * 0.20 +
        cashRunwayScore * 0.20 +
        growthScore * 0.10
      );
      
      return {
        overallScore: Math.round(healthScore),
        components: {
          liquidity: { score: Math.round(liquidityScore), value: currentRatio },
          profitability: { score: Math.round(profitabilityScore), value: netMargin },
          leverage: { score: Math.round(leverageScore), value: debtToEquity },
          cashRunway: { score: Math.round(cashRunwayScore), value: cashRunway },
          growth: { score: Math.round(growthScore), value: revenueGrowth }
        }
      };
    } catch (error) {
      console.error('Error calculating planning health score:', error);
      return null;
    }
  };

  // PHASE 2: Calculate Milestones
  const calculateMilestones = () => {
    if (!financialData || !advancedCalculations) {
      return null;
    }

    try {
      const milestones = [];
      const startYear = years && years.length > 0 ? Math.min(...years) : new Date().getFullYear();
      
      // Milestone 1: Plan Start
      milestones.push({
        id: 'start',
        title: 'Plan Start',
        date: `Year ${startYear}`,
        status: 'completed',
        description: 'Financial planning initiated'
      });
      
      // Milestone 2: Break-Even Point
      if (advancedCalculations?.breakEven?.break_even_analysis) {
        const breakEvenMonths = advancedCalculations.breakEven.break_even_analysis.break_even_months || 12;
        const breakEvenYear = startYear + Math.floor(breakEvenMonths / 12);
        const breakEvenMonth = breakEvenMonths % 12;
        milestones.push({
          id: 'breakeven',
          title: 'Break-Even',
          date: `Month ${breakEvenMonths} (Year ${breakEvenYear})`,
          status: breakEvenMonths <= 12 ? 'in-progress' : 'planned',
          description: `Revenue covers all costs`
        });
      }
      
      // Milestone 3: Profitability Point
      // Find first year with positive net income
      let profitabilityYear = null;
      for (const year of years || []) {
        const yearData = financialData[year];
        if (yearData?.financial_statements?.income_statement?.net_income > 0) {
          profitabilityYear = year;
          break;
        }
      }
      if (profitabilityYear) {
        milestones.push({
          id: 'profitability',
          title: 'Profitability',
          date: `Year ${profitabilityYear}`,
          status: profitabilityYear === startYear ? 'completed' : 'planned',
          description: 'Positive net income achieved'
        });
      }
      
      // Milestone 4: Growth Phase
      const midYear = years && years.length > 2 ? years[Math.floor(years.length / 2)] : startYear + 2;
      milestones.push({
        id: 'growth',
        title: 'Growth Phase',
        date: `Year ${midYear}`,
        status: 'planned',
        description: 'Expansion and scaling'
      });
      
      // Milestone 5: Plan Completion
      const endYear = years && years.length > 0 ? Math.max(...years) : startYear + 5;
      milestones.push({
        id: 'completion',
        title: 'Plan Completion',
        date: `Year ${endYear}`,
        status: 'planned',
        description: 'Strategic goals achieved'
      });
      
      return milestones;
    } catch (error) {
      console.error('Error calculating milestones:', error);
      return null;
    }
  };

  // PHASE 2: Load Scenario Comparison Data
  const loadScenarioComparison = async () => {
    if (!currentPlan?.id) return;
    
    try {
      const response = await axios.get(`${BACKEND_URL}/api/scenarios/${currentPlan.id}`);
      const scenarios = response.data || [];
      
      if (scenarios.length === 0) {
        setScenarioComparison(null);
        return;
      }
      
      // Get latest year for comparison
      const latestYear = years && years.length > 0 ? Math.max(...years) : null;
      const baseRevenue = latestYear && financialData[latestYear] 
        ? financialData[latestYear].financial_statements?.income_statement?.revenue || 0
        : 0;
      
      const comparisonData = scenarios.slice(0, 3).map(scenario => ({
        name: scenario.scenario_name || 'Unnamed Scenario',
        revenue: baseRevenue * (1 + (scenario.revenue_growth || 0) / 100),
        netIncome: baseRevenue * (1 + (scenario.revenue_growth || 0) / 100) * 0.15, // Estimate
        breakEvenMonth: Math.max(1, Math.round(12 / (1 + (scenario.revenue_growth || 0) / 100))),
        cashRunway: 12 + (scenario.cost_reduction || 0),
        roi: 15 + (scenario.revenue_growth || 0) / 2
      }));
      
      setScenarioComparison(comparisonData);
    } catch (error) {
      console.error('Error loading scenario comparison:', error);
      setScenarioComparison(null);
    }
  };

  // PHASE 2: Calculate Resource Allocation
  const calculateResourceAllocation = () => {
    if (!costData || costData.length === 0) {
      return null;
    }
    
    try {
      const allocation = {};
      let total = 0;
      
      costData.forEach(cost => {
        const category = cost.cost_category || 'Other';
        const amount = cost.amount || 0;
        allocation[category] = (allocation[category] || 0) + amount;
        total += amount;
      });
      
      // Convert to array with percentages
      const allocationArray = Object.entries(allocation).map(([name, value]) => ({
        name: name.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
        value: value,
        percentage: total > 0 ? (value / total) * 100 : 0
      }));
      
      return {
        data: allocationArray,
        total: total
      };
    } catch (error) {
      console.error('Error calculating resource allocation:', error);
      return null;
    }
  };

  // PHASE 2: Calculate Planning Coverage
  const calculatePlanningCoverage = () => {
    try {
      const coverage = [];
      
      // 1. Revenue Model Defined
      const revenueComplete = financialData && Object.keys(financialData).length > 0 ? 100 : 0;
      coverage.push({
        area: 'Revenue Model',
        percentage: revenueComplete,
        status: revenueComplete === 100 ? 'complete' : 'incomplete'
      });
      
      // 2. Cost Structure Defined
      const costComplete = costData && costData.length > 0 ? 100 : 0;
      coverage.push({
        area: 'Cost Structure',
        percentage: costComplete,
        status: costComplete === 100 ? 'complete' : 'incomplete'
      });
      
      // 3. Cash Flow Planning
      const cashFlowComplete = advancedCalculations?.cashFlow ? 100 : 0;
      coverage.push({
        area: 'Cash Flow Planning',
        percentage: cashFlowComplete,
        status: cashFlowComplete === 100 ? 'complete' : 'incomplete'
      });
      
      // 4. Break-Even Analysis
      const breakEvenComplete = advancedCalculations?.breakEven ? 100 : 0;
      coverage.push({
        area: 'Break-Even Analysis',
        percentage: breakEvenComplete,
        status: breakEvenComplete === 100 ? 'complete' : 'incomplete'
      });
      
      // 5. Strategic Initiatives (check if scenarios exist)
      const strategicComplete = scenarioComparison && scenarioComparison.length > 0 ? 100 : 20;
      coverage.push({
        area: 'Strategic Initiatives',
        percentage: strategicComplete,
        status: strategicComplete === 100 ? 'complete' : 'in-progress'
      });
      
      return coverage;
    } catch (error) {
      console.error('Error calculating planning coverage:', error);
      return null;
    }
  };

  // PHASE 2: Get Key Assumptions
  const getKeyAssumptions = () => {
    if (!financialData || !advancedCalculations) {
      return null;
    }
    
    try {
      const latestYear = years && years.length > 0 ? Math.max(...years) : null;
      const latestFinancial = latestYear ? financialData[latestYear] : null;
      
      if (!latestFinancial) return null;
      
      const keyMetrics = latestFinancial.financial_statements?.key_metrics || {};
      const income = latestFinancial.financial_statements?.income_statement || {};
      
      // Calculate growth rate
      let revenueGrowth = 0;
      if (years && years.length >= 2) {
        const prevYear = years[years.length - 2];
        const prevFinancial = financialData[prevYear];
        if (prevFinancial?.financial_statements?.income_statement?.revenue) {
          revenueGrowth = ((income.revenue - prevFinancial.financial_statements.income_statement.revenue) 
            / prevFinancial.financial_statements.income_statement.revenue) * 100;
        }
      }
      
      const monthlyCosts = costData.reduce((sum, cost) => sum + (cost.amount || 0), 0);
      
      return [
        {
          label: 'Revenue Growth Rate',
          value: `${revenueGrowth.toFixed(1)}%`,
          icon: TrendingUp
        },
        {
          label: 'Gross Margin',
          value: `${(keyMetrics.gross_margin_percent || 0).toFixed(1)}%`,
          icon: Percent
        },
        {
          label: 'Monthly Burn Rate',
          value: formatCurrency(monthlyCosts),
          icon: BurnIcon
        },
        {
          label: 'Cash Runway',
          value: `${Math.round(advancedCalculations?.cashFlow?.summary?.average_cash_runway_months || 0)} months`,
          icon: Clock
        },
        {
          label: 'Break-Even Timeline',
          value: `${Math.round(advancedCalculations?.breakEven?.break_even_analysis?.break_even_months || 0)} months`,
          icon: Target
        }
      ];
    } catch (error) {
      console.error('Error getting key assumptions:', error);
      return null;
    }
  };

  // Update Phase 2 data when dependencies change
  useEffect(() => {
    console.log('Phase 2 useEffect triggered', { 
      hasFinancialData: !!financialData, 
      hasAdvancedCalculations: !!advancedCalculations, 
      hasCostData: !!costData 
    });
    
    // Always calculate, even with partial data
    setPlanningHealthScore(calculatePlanningHealthScore());
    setMilestones(calculateMilestones());
    setResourceAllocation(calculateResourceAllocation());
    setPlanningCoverage(calculatePlanningCoverage());
    setKeyAssumptions(getKeyAssumptions());
  }, [financialData, advancedCalculations, costData, dashboardData, years]);

  useEffect(() => {
    loadDashboardData();
    if (currentPlan) {
      fetchFinancialManagementKPIs();
      calculateFinancialHealthKPIs();
      loadScenarioComparison();
    }
  }, [currentPlan?.id, years]);

  const fetchFinancialManagementKPIs = async () => {
    try {
      // Fetch integrated financial KPIs from Financial Management
      const response = await axios.get(`${BACKEND_URL}/api/financial-integration/kpis/${currentPlan.id}`);
      setFinancialKPIs(response.data.kpis);
      setIntegrationData(response.data.integration_data);
    } catch (error) {
      console.error('Error fetching financial management KPIs:', error);
      // Set empty data to prevent crashes
      setFinancialKPIs({
        leverage_ratios: {},
        profitability_ratios: {},
        market_ratios: {},
        capital_structure: {}
      });
      setIntegrationData({
        wacc: 0,
        debt_to_equity_ratio: 0,
        total_debt_balance: 0,
        total_equity_value: 0
      });
    }
  };

  // PHASE 1 TASK 2: Calculate Financial Health KPIs
  const calculateFinancialHealthKPIs = async () => {
    if (!currentPlan?.id || !years || years.length === 0) return;
    
    try {
      const currentYear = years[years.length - 1];
      const response = await axios.get(`${BACKEND_URL}/api/v3/calculations/financial-statements/${currentPlan.id}?year=${currentYear}`);
      const statements = response.data.financial_statements;
      
      const incomeStatement = statements?.income_statement || {};
      const balanceSheet = statements?.balance_sheet || {};
      const cashFlow = statements?.cash_flow || {};
      
      // Extract data
      const currentAssets = balanceSheet.current_assets || 0;
      const currentLiabilities = balanceSheet.current_liabilities || 0;
      const inventory = balanceSheet.inventory || 0;
      const totalDebt = balanceSheet.total_debt || 0;
      const totalEquity = balanceSheet.total_equity || 0;
      const cash = balanceSheet.cash || 0;
      const operatingCF = cashFlow.operating_cash_flow || 0;
      const operatingExpenses = incomeStatement.operating_expenses || 0;
      
      // Calculate KPIs
      const currentRatio = currentLiabilities > 0 ? currentAssets / currentLiabilities : 0;
      const quickRatio = currentLiabilities > 0 ? (currentAssets - inventory) / currentLiabilities : 0;
      const debtToEquity = totalEquity > 0 ? totalDebt / totalEquity : 0;
      const workingCapital = currentAssets - currentLiabilities;
      const operatingCFRatio = currentLiabilities > 0 ? operatingCF / currentLiabilities : 0;
      const monthlyBurnRate = operatingExpenses / 12;
      const cashRunway = monthlyBurnRate > 0 ? cash / monthlyBurnRate : 0;
      
      // Status calculations
      const currentRatioStatus = currentRatio >= 2.0 ? 'excellent' : currentRatio >= 1.5 ? 'good' : currentRatio >= 1.0 ? 'caution' : 'critical';
      const quickRatioStatus = quickRatio >= 1.0 ? 'excellent' : quickRatio >= 0.75 ? 'good' : quickRatio >= 0.5 ? 'caution' : 'critical';
      const debtToEquityStatus = debtToEquity <= 1.0 ? 'excellent' : debtToEquity <= 2.0 ? 'good' : debtToEquity <= 3.0 ? 'caution' : 'critical';
      const cashRunwayStatus = cashRunway >= 12 ? 'excellent' : cashRunway >= 6 ? 'good' : cashRunway >= 3 ? 'caution' : 'critical';
      
      setFinancialHealthKPIs({
        currentRatio,
        currentRatioStatus,
        quickRatio,
        quickRatioStatus,
        debtToEquity,
        debtToEquityStatus,
        workingCapital,
        operatingCFRatio,
        monthlyBurnRate,
        cashRunway,
        cashRunwayStatus,
        cash,
        year: currentYear
      });
    } catch (error) {
      console.error("Error calculating financial health KPIs:", error);
    }
  };
  
  // Helper function for status badges
  const getStatusBadge = (status) => {
    const badges = {
      excellent: { bg: 'bg-green-100', text: 'text-green-700', label: '🟢 Excellent' },
      good: { bg: 'bg-blue-100', text: 'text-blue-700', label: '🔵 Good' },
      caution: { bg: 'bg-yellow-100', text: 'text-yellow-700', label: '🟡 Caution' },
      critical: { bg: 'bg-red-100', text: 'text-red-700', label: '🔴 Critical' }
    };
    return badges[status] || badges.good;
  };

  if (loading) {
    return (
      <div className="p-8">
        <div className="flex items-center justify-center py-16">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-slate-600">Loading advanced dashboard...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!currentPlan) {
    return (
      <div className="p-8">
        <div className="flex items-center justify-center py-16">
          <div className="text-center max-w-md">
            <BarChart3 className="h-16 w-16 text-slate-400 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-slate-900 mb-2">No Business Plan Selected</h3>
            <p className="text-slate-600 mb-6">
              Please select or create a business plan to view KPIs and analytics.
            </p>
            <Button onClick={() => window.location.href = '/dashboard'} className="bg-blue-600 hover:bg-blue-700">
              Go to Dashboard
            </Button>
          </div>
        </div>
      </div>
    );
  }

  const { financial, performance, growth, health, multiYear } = dashboardData;

  return (
    <div className="p-8 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <TooltipLabel
            label={<h1 className="text-3xl font-bold text-slate-900">Advanced KPI Dashboard</h1>}
            tooltip={outputTooltips.kpis.kpi_dashboard}
            asChild
          />
          <p className="text-slate-600 mt-1">
            Comprehensive business intelligence and performance analytics for {currentPlan.company_name || currentPlan.name}
          </p>
        </div>
        <div className="flex items-center space-x-4">
          <Select value={selectedTimeframe} onValueChange={setSelectedTimeframe}>
            <SelectTrigger className="w-32">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="1y">1 Year</SelectItem>
              <SelectItem value="3y">3 Years</SelectItem>
              <SelectItem value="5y">5 Years</SelectItem>
            </SelectContent>
          </Select>
          <Button onClick={handleRefresh} variant="outline" size="sm" disabled={refreshing}>
            <RefreshCw className={`h-4 w-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <ExportButton
            targetId="kpi-dashboard-content"
            componentName={`KPI_Dashboard_${selectedTimeframe}`}
            formats={['png']}
            size="sm"
            variant="outline"
            showIcon={true}
            showText={true}
          />
        </div>
      </div>

      <div id="kpi-dashboard-content">
      {/* Key Performance Indicators Overview */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <TooltipLabel
                  label={<p className="text-sm text-blue-700 font-medium">Revenue (Annual)</p>}
                  tooltip={outputTooltips.kpis.annual_revenue_kpi}
                />
                <p className="text-2xl font-bold text-blue-900">
                  {formatCurrency(financial?.currentRevenue * 12, true)}
                </p>
                {growth?.revenueGrowth !== undefined && (
                  <div className="flex items-center mt-2">
                    {growth.revenueGrowth >= 0 ? (
                      <TrendingUp className="h-4 w-4 text-green-600 mr-1" />
                    ) : (
                      <TrendingDown className="h-4 w-4 text-red-600 mr-1" />
                    )}
                    <TooltipLabel
                      label={
                        <span className={`text-sm font-medium ${growth.revenueGrowth >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                          {Math.abs(growth.revenueGrowth).toFixed(1)}% YoY
                        </span>
                      }
                      tooltip={outputTooltips.kpis.yoy_growth}
                    />
                  </div>
                )}
              </div>
              <div className="p-3 bg-blue-200 rounded-full">
                <DollarSign className="h-6 w-6 text-blue-700" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-green-50 to-green-100 border-green-200">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <TooltipLabel
                  label={<p className="text-sm text-green-700 font-medium">Net Profit (Annual)</p>}
                  tooltip={outputTooltips.kpis.annual_profit_kpi}
                />
                <p className="text-2xl font-bold text-green-900">
                  {formatCurrency(financial?.currentProfit * 12, true)}
                </p>
                {growth?.profitGrowth !== undefined && (
                  <div className="flex items-center mt-2">
                    {growth.profitGrowth >= 0 ? (
                      <TrendingUp className="h-4 w-4 text-green-600 mr-1" />
                    ) : (
                      <TrendingDown className="h-4 w-4 text-red-600 mr-1" />
                    )}
                    <span className={`text-sm font-medium ${growth.profitGrowth >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {Math.abs(growth.profitGrowth).toFixed(1)}% YoY
                    </span>
                  </div>
                )}
              </div>
              <div className="p-3 bg-green-200 rounded-full">
                <TrendingUp className="h-6 w-6 text-green-700" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <TooltipLabel
                  label={<p className="text-sm text-purple-700 font-medium">Profit Margin</p>}
                  tooltip={outputTooltips.kpis.profit_margin_kpi}
                />
                <p className="text-2xl font-bold text-purple-900">
                  {formatPercentage(financial?.profitability)}
                </p>
                <div className="mt-2">
                  <div className="w-full bg-purple-200 rounded-full h-2">
                    <div 
                      className="bg-purple-600 h-2 rounded-full transition-all duration-500"
                      style={{ width: `${Math.min(100, Math.max(0, (financial?.profitability || 0) * 2))}%` }}
                    ></div>
                  </div>
                </div>
              </div>
              <div className="p-3 bg-purple-200 rounded-full">
                <Target className="h-6 w-6 text-purple-700" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-orange-50 to-orange-100 border-orange-200">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <TooltipLabel
                  label={<p className="text-sm text-orange-700 font-medium">Business Health</p>}
                  tooltip={outputTooltips.kpis.business_health_score}
                />
                <p className="text-2xl font-bold text-orange-900">
                  {health?.overallScore || 0}/100
                </p>
                <div className="mt-2">
                  <Badge 
                    variant={health?.overallScore >= 80 ? "success" : health?.overallScore >= 60 ? "warning" : "destructive"}
                  >
                    {health?.overallScore >= 80 ? "Excellent" : health?.overallScore >= 60 ? "Good" : "Needs Attention"}
                  </Badge>
                </div>
              </div>
              <div className="p-3 bg-orange-200 rounded-full">
                <Activity className="h-6 w-6 text-orange-700" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* PHASE 2: EXECUTIVE SUMMARY ENHANCEMENTS */}
      <div className="mb-8 space-y-6">
        <h2 className="text-2xl font-bold text-slate-900 flex items-center">
          <Zap className="h-6 w-6 mr-2 text-indigo-600" />
          Executive Summary
        </h2>

        {/* Widget 1: Planning Health Score */}
        <Card className="glass-card shadow-lg border-t-4 border-t-indigo-500">
          <CardHeader>
            <CardTitle className="flex items-center text-indigo-900">
              <Shield className="h-5 w-5 mr-2" />
              Planning Health Score
            </CardTitle>
            <CardDescription>Weighted composite metric of plan quality</CardDescription>
          </CardHeader>
          <CardContent>
            {planningHealthScore ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Circular Gauge */}
                <div className="flex flex-col items-center justify-center">
                  <div className="w-48 h-48">
                    <GaugeChart 
                      id="planning-health-gauge"
                      nrOfLevels={3}
                      colors={["#ef4444", "#f59e0b", "#10b981"]}
                      arcWidth={0.3}
                      percent={planningHealthScore.overallScore / 100}
                      textColor="#1e293b"
                      formatTextValue={() => `${planningHealthScore.overallScore}`}
                      animate={true}
                    />
                  </div>
                  <Badge 
                    variant={planningHealthScore.overallScore >= 80 ? "success" : planningHealthScore.overallScore >= 60 ? "warning" : "destructive"}
                    className="text-sm px-4 py-1 mt-2"
                  >
                    {planningHealthScore.overallScore >= 80 ? "Excellent Plan Health" : 
                     planningHealthScore.overallScore >= 60 ? "Good Plan Health" : 
                     "Needs Improvement"}
                  </Badge>
                </div>
                
                {/* Component Scores Breakdown */}
                <div className="space-y-3">
                  <h4 className="font-semibold text-sm text-slate-700 mb-3">Component Scores:</h4>
                  {Object.entries(planningHealthScore.components).map(([key, data]) => (
                    <div key={key} className="space-y-1">
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-slate-600 capitalize">{key.replace(/([A-Z])/g, ' $1').trim()}</span>
                        <span className="text-sm font-medium text-slate-700">{data.score}/100</span>
                      </div>
                      <div className="w-full bg-slate-200 rounded-full h-2">
                        <div 
                          className={`h-2 rounded-full ${data.score >= 80 ? 'bg-green-500' : data.score >= 60 ? 'bg-yellow-500' : 'bg-red-500'}`}
                          style={{ width: `${data.score}%` }}
                        ></div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="text-center py-8">
                <p className="text-slate-500">Calculating health score...</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Widget 2: Key Assumptions Summary */}
        <Card className="glass-card shadow-lg border-t-4 border-t-purple-500">
            <CardHeader>
              <CardTitle className="flex items-center text-purple-900">
                <Target className="h-5 w-5 mr-2" />
                Key Assumptions
              </CardTitle>
              <CardDescription>Critical planning parameters</CardDescription>
            </CardHeader>
            <CardContent>
              {keyAssumptions && keyAssumptions.length > 0 ? (
                <div className="space-y-3">
                  {keyAssumptions.map((assumption, index) => {
                    const Icon = assumption.icon;
                    return (
                      <div key={index} className="flex items-center justify-between bg-slate-50 p-3 rounded-lg hover:bg-slate-100 transition-colors">
                        <div className="flex items-center space-x-3">
                          <div className="bg-purple-100 p-2 rounded-full">
                            <Icon className="h-4 w-4 text-purple-600" />
                          </div>
                          <span className="text-sm font-medium text-slate-700">{assumption.label}</span>
                        </div>
                        <span className="text-sm font-bold text-purple-600">{assumption.value}</span>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-8">
                  <p className="text-slate-500">No assumptions available</p>
                </div>
              )}
            </CardContent>
          </Card>

        {/* Widget 3: Planning Coverage Indicator */}
        <Card className="glass-card shadow-lg border-t-4 border-t-green-500">
          <CardHeader>
            <CardTitle className="flex items-center text-green-900">
              <CheckCircle2 className="h-5 w-5 mr-2" />
              Planning Coverage
            </CardTitle>
            <CardDescription>Completeness of financial plan</CardDescription>
          </CardHeader>
          <CardContent>
            {planningCoverage && planningCoverage.length > 0 ? (
              <div className="space-y-4">
                {planningCoverage.map((area, index) => (
                  <div key={index} className="space-y-2">
                    <div className="flex justify-between items-center">
                      <span className="text-sm font-medium text-slate-700">{area.area}</span>
                      <div className="flex items-center space-x-2">
                        <span className="text-sm font-bold text-slate-900">{area.percentage}%</span>
                        {area.status === 'complete' ? (
                          <CheckCircle className="h-4 w-4 text-green-500" />
                        ) : area.status === 'in-progress' ? (
                          <Clock className="h-4 w-4 text-yellow-500" />
                        ) : (
                          <AlertTriangle className="h-4 w-4 text-red-500" />
                        )}
                      </div>
                    </div>
                    <div className="w-full bg-slate-200 rounded-full h-2.5">
                      <div 
                        className={`h-2.5 rounded-full transition-all duration-500 ${
                          area.status === 'complete' ? 'bg-green-500' : 
                          area.status === 'in-progress' ? 'bg-yellow-500' : 
                          'bg-red-500'
                        }`}
                        style={{ width: `${area.percentage}%` }}
                      ></div>
                    </div>
                  </div>
                ))}
                
                {/* Overall Progress */}
                <div className="mt-4 pt-4 border-t border-slate-200">
                  <div className="flex justify-between items-center">
                    <span className="text-sm font-bold text-slate-900">Overall Completion:</span>
                    <span className="text-lg font-bold text-green-600">
                      {Math.round(planningCoverage.reduce((sum, a) => sum + a.percentage, 0) / planningCoverage.length)}%
                    </span>
                  </div>
                </div>
              </div>
            ) : (
              <div className="text-center py-8">
                <p className="text-slate-500">Calculating coverage...</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Widget 4: Milestones Timeline */}
        <Card className="glass-card shadow-lg border-t-4 border-t-blue-500">
          <CardHeader>
            <CardTitle className="flex items-center text-blue-900">
              <Calendar className="h-5 w-5 mr-2" />
              Planning Milestones Timeline
            </CardTitle>
            <CardDescription>Key dates and strategic checkpoints</CardDescription>
          </CardHeader>
          <CardContent>
            {milestones && milestones.length > 0 ? (
              <div className="relative">
                {/* Timeline Line */}
                <div className="absolute top-8 left-0 right-0 h-1 bg-gradient-to-r from-blue-200 via-blue-400 to-blue-600"></div>
                
                {/* Milestones */}
                <div className="grid grid-cols-5 gap-4 relative">
                  {milestones.map((milestone, index) => (
                    <div key={milestone.id} className="flex flex-col items-center">
                      {/* Milestone Marker */}
                      <div className={`w-16 h-16 rounded-full flex items-center justify-center z-10 ${
                        milestone.status === 'completed' ? 'bg-green-500' :
                        milestone.status === 'in-progress' ? 'bg-yellow-500' :
                        'bg-blue-500'
                      } shadow-lg`}>
                        {milestone.status === 'completed' ? (
                          <CheckCircle className="h-8 w-8 text-white" />
                        ) : milestone.status === 'in-progress' ? (
                          <Clock className="h-8 w-8 text-white" />
                        ) : (
                          <Target className="h-8 w-8 text-white" />
                        )}
                      </div>
                      
                      {/* Milestone Info */}
                      <div className="mt-4 text-center">
                        <h4 className="font-bold text-sm text-slate-900">{milestone.title}</h4>
                        <p className="text-xs text-slate-600 mt-1">{milestone.date}</p>
                        <p className="text-xs text-slate-500 mt-1">{milestone.description}</p>
                        <Badge 
                          variant={
                            milestone.status === 'completed' ? "success" :
                            milestone.status === 'in-progress' ? "warning" :
                            "default"
                          }
                          className="mt-2 text-xs"
                        >
                          {milestone.status.replace('-', ' ').toUpperCase()}
                        </Badge>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="text-center py-8">
                <p className="text-slate-500">No milestones defined</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Widget 5: Scenario Quick Comparison */}
        <Card className="glass-card shadow-lg border-t-4 border-t-cyan-500">
            <CardHeader>
              <CardTitle className="flex items-center text-cyan-900">
                <BarChart3 className="h-5 w-5 mr-2" />
                Scenario Quick Comparison
              </CardTitle>
              <CardDescription>Top 5 KPIs across scenarios</CardDescription>
            </CardHeader>
            <CardContent>
              {scenarioComparison && scenarioComparison.length > 0 ? (
                <div className="space-y-4">
                  {/* Comparison Table */}
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-slate-100">
                        <tr>
                          <th className="text-left p-2 text-slate-700 font-semibold">Metric</th>
                          {scenarioComparison.map((scenario, idx) => (
                            <th key={idx} className="text-right p-2 text-slate-700 font-semibold">{scenario.name}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        <tr className="border-b border-slate-200">
                          <td className="p-2 font-medium text-slate-600">Revenue (Year 5)</td>
                          {scenarioComparison.map((scenario, idx) => (
                            <td key={idx} className="text-right p-2 text-slate-900">{formatCurrency(scenario.revenue, true)}</td>
                          ))}
                        </tr>
                        <tr className="border-b border-slate-200">
                          <td className="p-2 font-medium text-slate-600">Net Income</td>
                          {scenarioComparison.map((scenario, idx) => (
                            <td key={idx} className="text-right p-2 text-slate-900">{formatCurrency(scenario.netIncome, true)}</td>
                          ))}
                        </tr>
                        <tr className="border-b border-slate-200">
                          <td className="p-2 font-medium text-slate-600">Break-Even Month</td>
                          {scenarioComparison.map((scenario, idx) => (
                            <td key={idx} className="text-right p-2 text-slate-900">Month {scenario.breakEvenMonth}</td>
                          ))}
                        </tr>
                        <tr className="border-b border-slate-200">
                          <td className="p-2 font-medium text-slate-600">Cash Runway</td>
                          {scenarioComparison.map((scenario, idx) => (
                            <td key={idx} className="text-right p-2 text-slate-900">{scenario.cashRunway} mo</td>
                          ))}
                        </tr>
                        <tr>
                          <td className="p-2 font-medium text-slate-600">ROI</td>
                          {scenarioComparison.map((scenario, idx) => (
                            <td key={idx} className="text-right p-2 text-slate-900">{scenario.roi.toFixed(1)}%</td>
                          ))}
                        </tr>
                      </tbody>
                    </table>
                  </div>
                  
                  {/* Link to Scenario Modeling */}
                  <Button 
                    onClick={() => window.location.href = '/scenario-modeling'} 
                    className="w-full bg-cyan-600 hover:bg-cyan-700"
                  >
                    View Detailed Scenarios
                  </Button>
                </div>
              ) : (
                <div className="text-center py-8 space-y-4">
                  <p className="text-slate-500">No scenarios created yet</p>
                  <Button 
                    onClick={() => window.location.href = '/scenario-modeling'} 
                    className="bg-cyan-600 hover:bg-cyan-700"
                  >
                    Create Your First Scenario
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>

        {/* Widget 6: Resource Allocation Chart */}
        <Card className="glass-card shadow-lg border-t-4 border-t-amber-500">
          <CardHeader>
            <CardTitle className="flex items-center text-amber-900">
              <PieChart className="h-5 w-5 mr-2" />
              Resource Allocation
            </CardTitle>
            <CardDescription>Budget distribution by category</CardDescription>
          </CardHeader>
          <CardContent>
            {resourceAllocation && resourceAllocation.data && resourceAllocation.data.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Donut Chart */}
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <RechartsPieChart>
                      <Pie
                        data={resourceAllocation.data}
                        cx="50%"
                        cy="50%"
                        innerRadius={60}
                        outerRadius={90}
                        paddingAngle={5}
                        dataKey="value"
                      >
                        {resourceAllocation.data.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip formatter={(value) => formatCurrency(value)} />
                      <Legend />
                    </RechartsPieChart>
                  </ResponsiveContainer>
                </div>
                
                {/* Category Breakdown */}
                <div className="space-y-2">
                  <div className="flex justify-between items-center font-semibold text-slate-900 border-b pb-2 mb-3">
                    <span>Total Budget:</span>
                    <span className="text-amber-600">{formatCurrency(resourceAllocation.total)}</span>
                  </div>
                  {resourceAllocation.data.map((item, index) => (
                    <div key={index} className="flex justify-between items-center text-sm">
                      <div className="flex items-center space-x-2">
                        <div 
                          className="w-3 h-3 rounded-full" 
                          style={{ backgroundColor: COLORS[index % COLORS.length] }}
                        ></div>
                        <span className="text-slate-600">{item.name}:</span>
                      </div>
                      <span className="font-medium text-slate-900">{item.percentage.toFixed(1)}%</span>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="text-center py-8 space-y-4">
                <p className="text-slate-500">No cost data available</p>
                <Button 
                  onClick={() => window.location.href = '/cost-management'} 
                  className="bg-amber-600 hover:bg-amber-700"
                >
                  Add Cost Items
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* PHASE 1 TASK 2: FINANCIAL HEALTH METRICS SECTION */}
      {financialHealthKPIs && (
        <div className="mb-8">
          <h2 className="text-2xl font-bold text-slate-900 mb-6 flex items-center">
            <Activity className="h-6 w-6 mr-2 text-blue-600" />
            Financial Health Metrics ({financialHealthKPIs.year})
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {/* Current Ratio */}
            <Card className="glass-card shadow-soft border-l-4 border-l-blue-500">
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-slate-600 text-xs font-medium uppercase tracking-wide">Current Ratio</p>
                  <div className="bg-blue-100 rounded-full p-2">
                    <Activity className="h-4 w-4 text-blue-600" />
                  </div>
                </div>
                <p className="text-3xl font-bold text-slate-900 mb-2">
                  {financialHealthKPIs.currentRatio.toFixed(2)}
                </p>
                <div className="flex items-center justify-between">
                  <span className={`text-xs px-2 py-1 rounded-full ${getStatusBadge(financialHealthKPIs.currentRatioStatus).bg} ${getStatusBadge(financialHealthKPIs.currentRatioStatus).text} font-medium`}>
                    {getStatusBadge(financialHealthKPIs.currentRatioStatus).label}
                  </span>
                  <span className="text-xs text-slate-500">Liquidity</span>
                </div>
              </CardContent>
            </Card>

            {/* Quick Ratio */}
            <Card className="glass-card shadow-soft border-l-4 border-l-cyan-500">
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-slate-600 text-xs font-medium uppercase tracking-wide">Quick Ratio</p>
                  <div className="bg-cyan-100 rounded-full p-2">
                    <Zap className="h-4 w-4 text-cyan-600" />
                  </div>
                </div>
                <p className="text-3xl font-bold text-slate-900 mb-2">
                  {financialHealthKPIs.quickRatio.toFixed(2)}
                </p>
                <div className="flex items-center justify-between">
                  <span className={`text-xs px-2 py-1 rounded-full ${getStatusBadge(financialHealthKPIs.quickRatioStatus).bg} ${getStatusBadge(financialHealthKPIs.quickRatioStatus).text} font-medium`}>
                    {getStatusBadge(financialHealthKPIs.quickRatioStatus).label}
                  </span>
                  <span className="text-xs text-slate-500">Acid Test</span>
                </div>
              </CardContent>
            </Card>

            {/* Debt-to-Equity Ratio */}
            <Card className="glass-card shadow-soft border-l-4 border-l-purple-500">
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-slate-600 text-xs font-medium uppercase tracking-wide">Debt-to-Equity</p>
                  <div className="bg-purple-100 rounded-full p-2">
                    <Shield className="h-4 w-4 text-purple-600" />
                  </div>
                </div>
                <p className="text-3xl font-bold text-slate-900 mb-2">
                  {financialHealthKPIs.debtToEquity.toFixed(2)}
                </p>
                <div className="flex items-center justify-between">
                  <span className={`text-xs px-2 py-1 rounded-full ${getStatusBadge(financialHealthKPIs.debtToEquityStatus).bg} ${getStatusBadge(financialHealthKPIs.debtToEquityStatus).text} font-medium`}>
                    {getStatusBadge(financialHealthKPIs.debtToEquityStatus).label}
                  </span>
                  <span className="text-xs text-slate-500">Leverage</span>
                </div>
              </CardContent>
            </Card>

            {/* Working Capital */}
            <Card className="glass-card shadow-soft border-l-4 border-l-green-500">
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-slate-600 text-xs font-medium uppercase tracking-wide">Working Capital</p>
                  <div className="bg-green-100 rounded-full p-2">
                    <Wallet className="h-4 w-4 text-green-600" />
                  </div>
                </div>
                <p className="text-2xl font-bold text-slate-900 mb-2">
                  {formatCurrency(financialHealthKPIs.workingCapital)}
                </p>
                <div className="flex items-center justify-between">
                  <span className={`text-xs px-2 py-1 rounded-full ${financialHealthKPIs.workingCapital > 0 ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'} font-medium`}>
                    {financialHealthKPIs.workingCapital > 0 ? '🟢 Positive' : '🔴 Negative'}
                  </span>
                  <span className="text-xs text-slate-500">Operations</span>
                </div>
              </CardContent>
            </Card>

            {/* Cash Runway */}
            <Card className="glass-card shadow-soft border-l-4 border-l-orange-500">
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-slate-600 text-xs font-medium uppercase tracking-wide">Cash Runway</p>
                  <div className="bg-orange-100 rounded-full p-2">
                    <Clock className="h-4 w-4 text-orange-600" />
                  </div>
                </div>
                <p className="text-3xl font-bold text-slate-900 mb-2">
                  {financialHealthKPIs.cashRunway.toFixed(1)}
                  <span className="text-lg text-slate-500 ml-1">mo</span>
                </p>
                <div className="flex items-center justify-between">
                  <span className={`text-xs px-2 py-1 rounded-full ${getStatusBadge(financialHealthKPIs.cashRunwayStatus).bg} ${getStatusBadge(financialHealthKPIs.cashRunwayStatus).text} font-medium`}>
                    {getStatusBadge(financialHealthKPIs.cashRunwayStatus).label}
                  </span>
                  <span className="text-xs text-slate-500">Survival</span>
                </div>
              </CardContent>
            </Card>

            {/* Burn Rate */}
            <Card className="glass-card shadow-soft border-l-4 border-l-red-500">
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-slate-600 text-xs font-medium uppercase tracking-wide">Monthly Burn Rate</p>
                  <div className="bg-red-100 rounded-full p-2">
                    <BurnIcon className="h-4 w-4 text-red-600" />
                  </div>
                </div>
                <p className="text-2xl font-bold text-slate-900 mb-2">
                  {formatCurrency(financialHealthKPIs.monthlyBurnRate)}
                </p>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-slate-500">per month</span>
                  <span className="text-xs font-medium text-slate-700">Cash: {formatCurrency(financialHealthKPIs.cash, true)}</span>
                </div>
              </CardContent>
            </Card>

            {/* Operating Cash Flow Ratio */}
            <Card className="glass-card shadow-soft border-l-4 border-l-indigo-500">
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-slate-600 text-xs font-medium uppercase tracking-wide">Operating CF Ratio</p>
                  <div className="bg-indigo-100 rounded-full p-2">
                    <Percent className="h-4 w-4 text-indigo-600" />
                  </div>
                </div>
                <p className="text-3xl font-bold text-slate-900 mb-2">
                  {financialHealthKPIs.operatingCFRatio.toFixed(2)}
                </p>
                <div className="flex items-center justify-between">
                  <span className={`text-xs px-2 py-1 rounded-full ${financialHealthKPIs.operatingCFRatio >= 1.0 ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'} font-medium`}>
                    {financialHealthKPIs.operatingCFRatio >= 1.0 ? '🟢 Strong' : '🟡 Weak'}
                  </span>
                  <span className="text-xs text-slate-500">Cash Coverage</span>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      )}

      {/* Financial Management Integration Section */}
      {financialKPIs && integrationData && (
        <div className="mb-8">
          <h2 className="text-2xl font-bold text-slate-900 mb-6 flex items-center">
            <span className="mr-2">💰</span>
            Financial Management KPIs
            <Badge className="ml-2 bg-green-100 text-green-800">Live Data</Badge>
          </h2>
          
          {/* Financial Overview Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <TooltipLabel
                  label={<CardTitle className="text-sm font-medium">WACC</CardTitle>}
                  tooltip={outputTooltips.kpis.wacc}
                  asChild
                />
                <span className="text-2xl">📊</span>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-blue-600">
                  {(integrationData.wacc * 100).toFixed(2)}%
                </div>
                <p className="text-xs text-muted-foreground">
                  Cost of Capital
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <TooltipLabel
                  label={<CardTitle className="text-sm font-medium">Debt-to-Equity</CardTitle>}
                  tooltip={outputTooltips.kpis.debt_to_equity}
                  asChild
                />
                <span className="text-2xl">⚖️</span>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-orange-600">
                  {integrationData.debt_to_equity_ratio.toFixed(2)}
                </div>
                <p className="text-xs text-muted-foreground">
                  Leverage Ratio
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <TooltipLabel
                  label={<CardTitle className="text-sm font-medium">Total Debt</CardTitle>}
                  tooltip={outputTooltips.kpis.total_debt}
                  asChild
                />
                <span className="text-2xl">🏦</span>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-red-600">
                  {formatCurrencyContext(integrationData.total_debt_balance)}
                </div>
                <p className="text-xs text-muted-foreground">
                  Outstanding Loans
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <TooltipLabel
                  label={<CardTitle className="text-sm font-medium">Equity Value</CardTitle>}
                  tooltip={outputTooltips.kpis.equity_value}
                  asChild
                />
                <span className="text-2xl">💎</span>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-green-600">
                  {formatCurrencyContext(integrationData.total_equity_value)}
                </div>
                <p className="text-xs text-muted-foreground">
                  Market Capitalization
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Detailed Financial Ratios */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <TooltipLabel
                  label={<CardTitle>Leverage Ratios</CardTitle>}
                  tooltip={outputTooltips.kpis.leverage_ratios}
                  asChild
                />
                <CardDescription>Debt and leverage analysis from Financial Management</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span>Debt-to-Equity:</span>
                    <Badge className={financialKPIs.leverage_ratios.debt_to_equity > 2 ? 'bg-red-100 text-red-800' : 
                                    financialKPIs.leverage_ratios.debt_to_equity > 1 ? 'bg-yellow-100 text-yellow-800' : 
                                    'bg-green-100 text-green-800'}>
                      {financialKPIs.leverage_ratios.debt_to_equity?.toFixed(2) || 'N/A'}
                    </Badge>
                  </div>
                  <div className="flex justify-between items-center">
                    <span>Financial Leverage:</span>
                    <span className="font-medium">
                      {financialKPIs.leverage_ratios.financial_leverage?.toFixed(2) || 'N/A'}x
                    </span>
                  </div>
                  <TooltipLabel
                    label={
                      <div className="flex justify-between items-center">
                        <span>Times Interest Earned:</span>
                        <span className="font-medium">
                          {financialKPIs.leverage_ratios.times_interest_earned?.toFixed(2) || 'N/A'}
                        </span>
                      </div>
                    }
                    tooltip={outputTooltips.kpis.times_interest_earned}
                  />
                  <TooltipLabel
                    label={
                      <div className="flex justify-between items-center">
                        <span>DSCR:</span>
                        <Badge className={financialKPIs.leverage_ratios.debt_service_coverage < 1.25 ? 'bg-red-100 text-red-800' : 'bg-green-100 text-green-800'}>
                          {financialKPIs.leverage_ratios.debt_service_coverage?.toFixed(2) || 'N/A'}
                        </Badge>
                      </div>
                    }
                    tooltip={outputTooltips.kpis.dscr}
                  />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Profitability & Market Ratios</CardTitle>
                <CardDescription>Performance metrics from integrated data</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <TooltipLabel
                    label={
                      <div className="flex justify-between items-center">
                        <span>Return on Equity:</span>
                        <span className="font-medium text-green-600">
                          {(financialKPIs.profitability_ratios.return_on_equity * 100)?.toFixed(2) || 'N/A'}%
                        </span>
                      </div>
                    }
                    tooltip={outputTooltips.kpis.return_on_equity}
                  />
                  <TooltipLabel
                    label={
                      <div className="flex justify-between items-center">
                        <span>ROA:</span>
                        <span className="font-medium">
                          {(financialKPIs.profitability_ratios.return_on_assets * 100)?.toFixed(2) || 'N/A'}%
                        </span>
                      </div>
                    }
                    tooltip={outputTooltips.kpis.return_on_assets}
                  />
                  <TooltipLabel
                    label={
                      <div className="flex justify-between items-center">
                        <span>P/E Ratio:</span>
                        <span className="font-medium">
                          {financialKPIs.market_ratios.price_to_earnings?.toFixed(2) || 'N/A'}
                        </span>
                      </div>
                    }
                    tooltip={outputTooltips.kpis.pe_ratio}
                  />
                  <TooltipLabel
                    label={
                      <div className="flex justify-between items-center">
                        <span>Dividend Yield:</span>
                        <span className="font-medium text-blue-600">
                          {(financialKPIs.market_ratios.dividend_yield * 100)?.toFixed(2) || 'N/A'}%
                        </span>
                      </div>
                    }
                    tooltip={outputTooltips.kpis.dividend_yield}
                  />
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      )}

      {/* Advanced Analytics Tabs */}
      <Tabs defaultValue="performance" className="space-y-6">
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="performance">Performance</TabsTrigger>
          <TabsTrigger value="financial">Financial Health</TabsTrigger>
          <TabsTrigger value="efficiency">Operational</TabsTrigger>
          <TabsTrigger value="forecast">Forecasting</TabsTrigger>
        </TabsList>

        {/* Performance Analytics */}
        <TabsContent value="performance" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Revenue & Profit Trend */}
            <ChartWithExport
              chartId="revenue-profitability-trend-chart"
              chartName="Revenue_Profitability_Trend"
              formats={['png']}
              exportPosition="top-right"
            >
              <Card>
                <CardHeader>
                  <TooltipLabel
                    label={<CardTitle>Revenue & Profitability Trend</CardTitle>}
                    tooltip={outputTooltips.kpis.revenue_profitability_trend}
                    asChild
                  />
                  <CardDescription>{years.length}-year financial performance trajectory</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="h-80">
                    <ResponsiveContainer width="100%" height="100%">
                      <ComposedChart data={multiYear}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                        <XAxis dataKey="year" stroke="#64748b" />
                        <YAxis stroke="#64748b" />
                        <Tooltip 
                          formatter={(value, name) => [
                            name.includes('Margin') ? `${value.toFixed(1)}%` : formatCurrency(value, true),
                            name.replace(/([A-Z])/g, ' $1').trim()
                          ]}
                        />
                        <Legend />
                        <Bar dataKey="revenue" fill="#3b82f6" name="Revenue" />
                        <Bar dataKey="netIncome" fill="#10b981" name="Net Income" />
                        <Line 
                          type="monotone" 
                          dataKey="grossMargin" 
                          stroke="#f59e0b" 
                          strokeWidth={2}
                          name="Gross Margin"
                        />
                      </ComposedChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>
            </ChartWithExport>

            {/* Profitability Gauges */}
            <Card>
              <CardHeader>
                <TooltipLabel
                  label={<CardTitle>Profitability Metrics</CardTitle>}
                  tooltip={outputTooltips.kpis.profitability_gauges}
                  asChild
                />
                <CardDescription>Current year margin performance</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-6">
                  <div className="text-center">
                    <div className="w-24 h-24 mx-auto mb-3">
                      <CircularProgressbar
                        value={Math.min(100, Math.max(0, financial?.profitability * 2 || 0))}
                        text={formatPercentage(financial?.profitability)}
                        styles={buildStyles({
                          textSize: '16px',
                          pathColor: '#10b981',
                          textColor: '#374151',
                          trailColor: '#e5e7eb'
                        })}
                      />
                    </div>
                    <p className="text-sm text-slate-600 font-medium">Net Margin</p>
                  </div>
                  
                  <div className="text-center">
                    <div className="w-24 h-24 mx-auto mb-3">
                      {health?.overallScore !== undefined && (
                        <GaugeChart 
                          id="health-gauge"
                          nrOfLevels={3}
                          colors={['#ef4444', '#f59e0b', '#10b981']}
                          arcWidth={0.3}
                          percent={health.overallScore / 100}
                          textColor="#374151"
                          hideText={false}
                        />
                      )}
                    </div>
                    <p className="text-sm text-slate-600 font-medium">Health Score</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Cost Breakdown */}
          <Card>
            <CardHeader>
              <TooltipLabel
                label={<CardTitle>Cost Structure Analysis</CardTitle>}
                tooltip={outputTooltips.kpis.cost_structure}
                asChild
              />
              <CardDescription>Monthly cost breakdown by category</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <RechartsPieChart>
                      <Pie
                        data={Object.entries(performance?.costByCategory || {}).map(([category, amount]) => ({
                          name: category.charAt(0).toUpperCase() + category.slice(1).replace('_', ' '),
                          value: amount
                        }))}
                        dataKey="value"
                        nameKey="name"
                        cx="50%"
                        cy="50%"
                        outerRadius={80}
                        label={({name, percent}) => `${name} ${(percent * 100).toFixed(1)}%`}
                      >
                        {Object.entries(performance?.costByCategory || {}).map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip formatter={(value) => formatCurrency(value)} />
                    </RechartsPieChart>
                  </ResponsiveContainer>
                </div>
                
                <div className="space-y-4">
                  <TooltipLabel
                    label={
                      <div className="bg-slate-50 p-4 rounded-lg">
                        <div className="flex justify-between items-center">
                          <span className="text-sm font-medium text-slate-700">Total Monthly Costs</span>
                          <span className="text-lg font-bold text-slate-900">
                            {formatCurrency(performance?.totalMonthlyCosts)}
                          </span>
                        </div>
                      </div>
                    }
                    tooltip={outputTooltips.kpis.monthly_costs}
                  />
                  <TooltipLabel
                    label={
                      <div className="bg-slate-50 p-4 rounded-lg">
                        <div className="flex justify-between items-center">
                          <span className="text-sm font-medium text-slate-700">Annual Projection</span>
                          <span className="text-lg font-bold text-slate-900">
                            {formatCurrency(performance?.annualCostProjection, true)}
                          </span>
                        </div>
                      </div>
                    }
                    tooltip={outputTooltips.kpis.annual_cost_projection}
                  />
                  <div className="bg-slate-50 p-4 rounded-lg">
                    <div className="flex justify-between items-center">
                      <span className="text-sm font-medium text-slate-700">Cost Items Tracked</span>
                      <span className="text-lg font-bold text-slate-900">
                        {performance?.costItemCount || 0}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Financial Health Tab */}
        <TabsContent value="financial">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <Card>
              <CardHeader>
                <TooltipLabel
                  label={<CardTitle>Cash Runway</CardTitle>}
                  tooltip={outputTooltips.kpis.cash_runway}
                  asChild
                />
                <CardDescription>Months of operating runway</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="text-center">
                  <div className="text-4xl font-bold text-blue-600 mb-2">
                    {Math.round(financial?.cashRunway || 0)}
                  </div>
                  <p className="text-sm text-slate-600">Months</p>
                  <div className="mt-4">
                    <div className="w-full bg-slate-200 rounded-full h-2">
                      <div 
                        className="bg-blue-600 h-2 rounded-full transition-all duration-500"
                        style={{ width: `${Math.min(100, Math.max(0, (financial?.cashRunway || 0) / 24 * 100))}%` }}
                      ></div>
                    </div>
                    <p className="text-xs text-slate-500 mt-1">Target: 24+ months</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <TooltipLabel
                  label={<CardTitle>Break-Even Point</CardTitle>}
                  tooltip={outputTooltips.kpis.break_even_units}
                  asChild
                />
                <CardDescription>Units needed to break even</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="text-center">
                  <div className="text-4xl font-bold text-green-600 mb-2">
                    {Math.round(dashboardData.efficiency?.breakEvenUnits || 0).toLocaleString()}
                  </div>
                  <p className="text-sm text-slate-600">Units</p>
                  <div className="mt-4">
                    <Badge variant="outline" className="text-green-700 border-green-200">
                      Revenue: {formatCurrency(dashboardData.efficiency?.breakEvenRevenue, true)}
                    </Badge>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <TooltipLabel
                  label={<CardTitle>Safety Margin</CardTitle>}
                  tooltip={outputTooltips.kpis.margin_of_safety}
                  asChild
                />
                <CardDescription>Margin of safety percentage</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="text-center">
                  <div className="text-4xl font-bold text-orange-600 mb-2">
                    {formatPercentage(dashboardData.efficiency?.marginOfSafety)}
                  </div>
                  <p className="text-sm text-slate-600">Safety Buffer</p>
                  <div className="mt-4">
                    <div className="w-full bg-slate-200 rounded-full h-2">
                      <div 
                        className="bg-orange-600 h-2 rounded-full transition-all duration-500"
                        style={{ width: `${Math.min(100, Math.max(0, dashboardData.efficiency?.marginOfSafety * 2 || 0))}%` }}
                      ></div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Operational Efficiency Tab */}
        <TabsContent value="efficiency" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Cost Efficiency Metrics */}
            <Card>
              <CardHeader>
                <TooltipLabel
                  label={
                    <CardTitle className="flex items-center">
                      <DollarSign className="h-5 w-5 mr-2 text-green-600" />
                      Cost Efficiency
                    </CardTitle>
                  }
                  tooltip={outputTooltips.kpis.cost_efficiency_score}
                  asChild
                />
                <CardDescription>Cost management and optimization</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <TooltipLabel
                    label={
                      <div>
                        <div className="flex justify-between items-center mb-2">
                          <span className="text-sm font-medium text-slate-600">Cost per Revenue Dollar</span>
                          <span className="text-lg font-bold text-slate-900">
                            ${((performance?.totalMonthlyCosts || 0) / Math.max(financial?.currentRevenue || 1, 1)).toFixed(2)}
                          </span>
                        </div>
                        <div className="w-full bg-slate-200 rounded-full h-2">
                          <div 
                            className="bg-green-600 h-2 rounded-full transition-all duration-500"
                            style={{ width: `${Math.min(100, Math.max(0, 100 - (((performance?.totalMonthlyCosts || 0) / Math.max(financial?.currentRevenue || 1, 1)) * 100)))}%` }}
                          ></div>
                        </div>
                      </div>
                    }
                    tooltip={outputTooltips.kpis.cost_per_revenue_dollar}
                  />
                  
                  <TooltipLabel
                    label={
                      <div>
                        <div className="flex justify-between items-center mb-2">
                          <span className="text-sm font-medium text-slate-600">Operating Ratio</span>
                          <span className="text-lg font-bold text-slate-900">
                            {((performance?.totalMonthlyCosts || 0) / Math.max(financial?.currentRevenue || 1, 1) * 100).toFixed(1)}%
                          </span>
                        </div>
                        <Badge variant={((performance?.totalMonthlyCosts || 0) / Math.max(financial?.currentRevenue || 1, 1) * 100) < 80 ? "success" : "warning"}>
                          {((performance?.totalMonthlyCosts || 0) / Math.max(financial?.currentRevenue || 1, 1) * 100) < 80 ? "Efficient" : "Needs Optimization"}
                        </Badge>
                      </div>
                    }
                    tooltip={outputTooltips.kpis.operating_ratio_kpi}
                  />

                  <div>
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-sm font-medium text-slate-600">Cost Items Managed</span>
                      <span className="text-lg font-bold text-slate-900">{performance?.costItemCount || 0}</span>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Resource Utilization */}
            <Card>
              <CardHeader>
                <TooltipLabel
                  label={
                    <CardTitle className="flex items-center">
                      <Users className="h-5 w-5 mr-2 text-blue-600" />
                      Resource Utilization
                    </CardTitle>
                  }
                  tooltip={outputTooltips.kpis.resource_utilization}
                  asChild
                />
                <CardDescription>Efficiency of resource allocation</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="text-center">
                    <div className="w-20 h-20 mx-auto mb-3">
                      <CircularProgressbar
                        value={Math.min(100, Math.max(0, 75 + (Math.random() * 20)))} // Simulated efficiency score
                        text={`${Math.round(75 + (Math.random() * 20))}%`}
                        styles={buildStyles({
                          textSize: '16px',
                          pathColor: '#3b82f6',
                          textColor: '#374151',
                          trailColor: '#e5e7eb'
                        })}
                      />
                    </div>
                    <p className="text-sm text-slate-600 font-medium">Resource Efficiency</p>
                  </div>

                  <div className="space-y-3">
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-slate-600">Asset Utilization</span>
                      <span className="text-sm font-medium text-slate-900">
                        {(70 + (Math.random() * 25)).toFixed(1)}%
                      </span>
                    </div>
                    
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-slate-600">Capacity Usage</span>
                      <span className="text-sm font-medium text-slate-900">
                        {(60 + (Math.random() * 30)).toFixed(1)}%
                      </span>
                    </div>

                    <div className="flex justify-between items-center">
                      <span className="text-sm text-slate-600">Productivity Index</span>
                      <span className="text-sm font-medium text-slate-900">
                        {(80 + (Math.random() * 15)).toFixed(1)}%
                      </span>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Performance Indicators */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <Activity className="h-5 w-5 mr-2 text-orange-600" />
                  Performance KPIs
                </CardTitle>
                <CardDescription>Key performance benchmarks</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {[
                    { 
                      label: "Break-Even Efficiency", 
                      value: dashboardData.efficiency?.breakEvenUnits ? 
                        Math.min(100, (10000 / Math.max(dashboardData.efficiency.breakEvenUnits, 1)) * 100).toFixed(1) : "0.0",
                      target: "85+",
                      color: "text-green-600"
                    },
                    { 
                      label: "Margin Optimization", 
                      value: dashboardData.efficiency?.marginOfSafety?.toFixed(1) || "0.0",
                      target: "20+%", 
                      color: "text-blue-600"
                    },
                    { 
                      label: "Cash Efficiency", 
                      value: financial?.cashRunway > 12 ? "85.0" : financial?.cashRunway > 6 ? "65.0" : "35.0",
                      target: "75+",
                      color: financial?.cashRunway > 12 ? "text-green-600" : "text-orange-600"
                    }
                  ].map((metric, index) => (
                    <div key={index} className="bg-slate-50 p-3 rounded-lg">
                      <div className="flex justify-between items-center mb-2">
                        <span className="text-sm font-medium text-slate-700">{metric.label}</span>
                        <span className={`text-lg font-bold ${metric.color}`}>
                          {metric.value}%
                        </span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-xs text-slate-500">Target: {metric.target}</span>
                        <Badge variant={parseFloat(metric.value) >= 75 ? "success" : parseFloat(metric.value) >= 50 ? "warning" : "destructive"} className="text-xs">
                          {parseFloat(metric.value) >= 75 ? "Excellent" : parseFloat(metric.value) >= 50 ? "Good" : "Needs Focus"}
                        </Badge>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Department-Level Analysis */}
          <Card>
            <CardHeader>
              <CardTitle>Department Efficiency Analysis</CardTitle>
              <CardDescription>Cost allocation and efficiency by department</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={[
                    { department: 'Sales & Marketing', efficiency: 85, budget: 45000, actual: 42000 },
                    { department: 'Operations', efficiency: 78, budget: 35000, actual: 38000 },
                    { department: 'Technology', efficiency: 92, budget: 55000, actual: 48000 },
                    { department: 'Finance & Admin', efficiency: 88, budget: 25000, actual: 23000 },
                    { department: 'Human Resources', efficiency: 75, budget: 20000, actual: 22000 }
                  ]}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                    <XAxis dataKey="department" stroke="#64748b" fontSize={12} />
                    <YAxis stroke="#64748b" />
                    <Tooltip 
                      formatter={(value, name) => [
                        name === 'efficiency' ? `${value}%` : formatCurrency(value),
                        name === 'efficiency' ? 'Efficiency Score' : name === 'budget' ? 'Budget' : 'Actual Spend'
                      ]}
                    />
                    <Legend />
                    <Bar dataKey="efficiency" fill="#10b981" name="Efficiency %" />
                    <Bar dataKey="budget" fill="#3b82f6" name="Budget" />
                    <Bar dataKey="actual" fill="#f59e0b" name="Actual" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Forecasting Tab */}
        <TabsContent value="forecast" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Revenue Forecasting */}
            <Card>
              <CardHeader>
                <TooltipLabel
                  label={
                    <CardTitle className="flex items-center">
                      <TrendingUp className="h-5 w-5 mr-2 text-green-600" />
                      Revenue Forecasting
                    </CardTitle>
                  }
                  tooltip={outputTooltips.kpis.revenue_forecast}
                  asChild
                />
                <CardDescription>Predictive revenue analysis and trends</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={(() => {
                      // Generate forecasting data based on current trends
                      const forecastData = [];
                      const baseRevenue = financial?.currentRevenue || 10000;
                      const growthRate = Math.max(growth?.revenueGrowth || 5, 2); // Minimum 2% growth
                      
                      for (let i = 0; i <= 24; i++) { // Next 24 months
                        const month = new Date();
                        month.setMonth(month.getMonth() + i);
                        const monthName = month.toLocaleDateString('en-US', { month: 'short', year: '2-digit' });
                        
                        // Calculate forecasted revenue with some variation
                        const trend = baseRevenue * Math.pow(1 + (growthRate / 100), i / 12);
                        const seasonal = 1 + (Math.sin(i * Math.PI / 6) * 0.1); // Seasonal variation
                        const forecast = trend * seasonal;
                        
                        // Add confidence intervals
                        const confidence = forecast * 0.15; // ±15% confidence
                        
                        forecastData.push({
                          month: monthName,
                          forecast: Math.round(forecast),
                          upper: Math.round(forecast + confidence),
                          lower: Math.round(forecast - confidence),
                          actual: i === 0 ? baseRevenue : null
                        });
                      }
                      return forecastData;
                    })()}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                      <XAxis dataKey="month" stroke="#64748b" fontSize={11} />
                      <YAxis stroke="#64748b" />
                      <Tooltip formatter={(value) => formatCurrency(value)} />
                      <Legend />
                      <Area
                        dataKey="upper"
                        stackId="confidence"
                        stroke="#10b981"
                        fill="#10b981"
                        fillOpacity={0.1}
                        name="Confidence Upper"
                      />
                      <Area
                        dataKey="lower"
                        stackId="confidence"
                        stroke="#10b981"
                        fill="#ffffff"
                        fillOpacity={0.1}
                        name="Confidence Lower"
                      />
                      <Line
                        type="monotone"
                        dataKey="forecast"
                        stroke="#10b981"
                        strokeWidth={3}
                        name="Forecasted Revenue"
                      />
                      <Line
                        type="monotone"
                        dataKey="actual"
                        stroke="#3b82f6"
                        strokeWidth={2}
                        strokeDasharray="5 5"
                        name="Actual Revenue"
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            {/* Growth Predictions */}
            <Card>
              <CardHeader>
                <TooltipLabel
                  label={
                    <CardTitle className="flex items-center">
                      <Target className="h-5 w-5 mr-2 text-blue-600" />
                      Growth Predictions
                    </CardTitle>
                  }
                  tooltip={outputTooltips.kpis.growth_predictions}
                  asChild
                />
                <CardDescription>Future growth trajectory analysis</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-6">
                  {/* Growth Rate Gauge */}
                  <div className="text-center">
                    <div className="w-32 h-16 mx-auto mb-3">
                      <GaugeChart 
                        id="growth-forecast-gauge"
                        nrOfLevels={4}
                        colors={['#ef4444', '#f59e0b', '#10b981', '#06b6d4']}
                        arcWidth={0.25}
                        percent={Math.min(1, Math.max(0, (growth?.revenueGrowth || 5) / 50))}
                        textColor="#374151"
                        hideText={false}
                      />
                    </div>
                    <TooltipLabel
                      label={
                        <>
                          <p className="text-sm text-slate-600 font-medium">Predicted Growth Rate</p>
                          <p className="text-xl font-bold text-slate-900">{(growth?.revenueGrowth || 5).toFixed(1)}%</p>
                        </>
                      }
                      tooltip={outputTooltips.kpis.predicted_growth_rate}
                      asChild
                    />
                  </div>

                  {/* Growth Metrics */}
                  <div className="space-y-3">
                    {[
                      {
                        label: "12-Month Revenue Target",
                        value: formatCurrency((financial?.currentRevenue || 10000) * 12 * (1 + (growth?.revenueGrowth || 5) / 100), true),
                        trend: "up",
                        tooltip: outputTooltips.kpis.revenue_target
                      },
                      {
                        label: "Break-Even Timeline",
                        value: dashboardData.efficiency?.breakEvenUnits > 0 ? 
                          `${Math.round(12 / Math.max(1, (dashboardData.efficiency.breakEvenUnits / 1000)))} months` : 
                          "6-9 months",
                        trend: "stable",
                        tooltip: outputTooltips.kpis.break_even_timeline
                      },
                      {
                        label: "Profitability Forecast",
                        value: `${((financial?.profitability || 0) * 1.2).toFixed(1)}%`,
                        trend: "up",
                        tooltip: outputTooltips.kpis.profitability_forecast
                      }
                    ].map((metric, index) => (
                      <TooltipLabel
                        key={index}
                        label={
                          <div className="bg-slate-50 p-3 rounded-lg">
                            <div className="flex justify-between items-center">
                              <span className="text-sm font-medium text-slate-700">{metric.label}</span>
                              <div className="flex items-center space-x-2">
                                <span className="text-sm font-bold text-slate-900">{metric.value}</span>
                                {metric.trend === "up" && <ArrowUpRight className="h-4 w-4 text-green-600" />}
                                {metric.trend === "down" && <ArrowDownRight className="h-4 w-4 text-red-600" />}
                                {metric.trend === "stable" && <Activity className="h-4 w-4 text-slate-600" />}
                              </div>
                            </div>
                          </div>
                        }
                        tooltip={metric.tooltip}
                      />
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Risk Assessment & Scenarios */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Risk Analysis */}
            <Card>
              <CardHeader>
                <TooltipLabel
                  label={
                    <CardTitle className="flex items-center">
                      <AlertTriangle className="h-5 w-5 mr-2 text-orange-600" />
                      Risk Assessment
                    </CardTitle>
                  }
                  tooltip={outputTooltips.kpis.risk_assessment}
                  asChild
                />
                <CardDescription>Business risk analysis and mitigation</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {[
                    {
                      risk: "Cash Flow Risk",
                      level: financial?.cashRunway < 6 ? "High" : financial?.cashRunway < 12 ? "Medium" : "Low",
                      probability: financial?.cashRunway < 6 ? 75 : financial?.cashRunway < 12 ? 35 : 15,
                      impact: "High"
                    },
                    {
                      risk: "Market Competition",
                      level: "Medium",
                      probability: 45,
                      impact: "Medium"
                    },
                    {
                      risk: "Cost Inflation", 
                      level: performance?.totalMonthlyCosts > 50000 ? "High" : "Medium",
                      probability: performance?.totalMonthlyCosts > 50000 ? 65 : 40,
                      impact: "Medium"
                    },
                    {
                      risk: "Revenue Concentration",
                      level: "Low",
                      probability: 25,
                      impact: "Medium"
                    }
                  ].map((risk, index) => (
                    <div key={index} className="border border-slate-200 rounded-lg p-3">
                      <div className="flex justify-between items-center mb-2">
                        <span className="font-medium text-slate-900">{risk.risk}</span>
                        <Badge variant={risk.level === "High" ? "destructive" : risk.level === "Medium" ? "warning" : "success"}>
                          {risk.level}
                        </Badge>
                      </div>
                      <div className="flex justify-between text-sm text-slate-600">
                        <span>Probability: {risk.probability}%</span>
                        <span>Impact: {risk.impact}</span>
                      </div>
                      <div className="w-full bg-slate-200 rounded-full h-1.5 mt-2">
                        <div 
                          className={`h-1.5 rounded-full transition-all duration-500 ${
                            risk.level === "High" ? "bg-red-500" : 
                            risk.level === "Medium" ? "bg-orange-500" : "bg-green-500"
                          }`}
                          style={{ width: `${risk.probability}%` }}
                        ></div>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Interactive Scenario Modeling */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <Zap className="h-5 w-5 mr-2 text-purple-600" />
                  Interactive Scenario Modeling
                </CardTitle>
                <CardDescription>Advanced what-if analysis with real-time calculations</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="text-center p-4 bg-gradient-to-r from-purple-50 to-blue-50 rounded-lg border border-purple-200">
                    <CheckCircle className="h-8 w-8 text-green-600 mx-auto mb-2" />
                    <h4 className="font-semibold text-slate-900 mb-2">Full Scenario Analysis Available</h4>
                    <p className="text-sm text-slate-600 mb-4">
                      Interactive parameter tuning with 5 variables and real-time impact visualization.
                    </p>
                    <div className="grid grid-cols-3 gap-2 text-xs">
                      <div className="bg-white p-2 rounded border">
                        <div className="font-semibold text-green-600">Optimistic</div>
                        <div className="text-slate-600">+25% revenue</div>
                      </div>
                      <div className="bg-white p-2 rounded border">
                        <div className="font-semibold text-blue-600">Realistic</div>
                        <div className="text-slate-600">+5% growth</div>
                      </div>
                      <div className="bg-white p-2 rounded border">
                        <div className="font-semibold text-orange-600">Pessimistic</div>
                        <div className="text-slate-600">-15% impact</div>
                      </div>
                    </div>
                    <Button 
                      onClick={() => window.location.href = '/scenario-modeling'} 
                      className="mt-3 bg-purple-600 hover:bg-purple-700"
                    >
                      <Zap className="h-4 w-4 mr-2" />
                      Launch Scenario Modeling
                    </Button>
                  </div>

                  {/* Live Scenario Metrics */}
                  <div className="space-y-3">
                    <h5 className="font-medium text-slate-900">Current Scenario Capabilities</h5>
                    {[
                      {
                        feature: "Revenue Growth Modeling",
                        description: "Interactive slider adjustment"
                      },
                      {
                        feature: "Cost Reduction Analysis", 
                        description: "Real-time savings calculation"
                      },
                      {
                        feature: "Market Expansion Impact",
                        description: "Growth potential assessment"
                      },
                      {
                        feature: "Risk-Return Matrix",
                        description: "Probability vs impact analysis"
                      }
                    ].map((feature, index) => (
                      <div key={index} className="flex justify-between items-center bg-slate-50 p-2 rounded">
                        <span className="text-sm font-medium text-slate-700">{feature.feature}</span>
                        <span className="text-xs text-slate-500">{feature.description}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
      </div>
    </div>
  );
};

export default EnhancedKPIDashboard;